import { useCallback, useState } from 'react'

import { GuestsLimits } from '../config/guests'
import type { GuestsState } from '../model'

interface UseSelectGuestsProps {
  initialState?: GuestsState
  onGuestsChange?: (guests: GuestsState) => void
}

export const useSelectGuests = ({
  initialState,
  onGuestsChange,
}: UseSelectGuestsProps = {}) => {
  const [adults, setAdults] = useState(
    initialState?.adults || GuestsLimits.adults.default,
  )
  const [children, setChildren] = useState(
    initialState?.children || GuestsLimits.children.default,
  )
  const [childrenAges, setChildrenAges] = useState<number[]>(
    initialState?.childrenAges || [],
  )
  const [showChildrenBlock, setShowChildrenBlock] = useState(
    !!initialState?.children && initialState.children > 0,
  )

  const updateGuests = useCallback(
    (newAdults: number, newChildren: number, newAges: number[]) => {
      onGuestsChange?.({
        adults: newAdults,
        children: newChildren,
        childrenAges: newAges,
      })
    },
    [onGuestsChange],
  )

  const handleChangeAdults = useCallback(
    (value: number) => {
      setAdults(value)
      updateGuests(value, children, childrenAges)
    },
    [children, childrenAges, updateGuests],
  )

  const handleChangeChildren = useCallback(
    (value: number) => {
      setChildren(value)

      let newAges = childrenAges

      // Удаляем лишние возраста если детей стало меньше
      if (value < childrenAges.length) {
        newAges = childrenAges.slice(0, value)
        setChildrenAges(newAges)
      }
      // Добавляем дефолтные возраста если детей стало больше
      else if (value > childrenAges.length) {
        newAges = [
          ...childrenAges,
          ...Array(value - childrenAges.length).fill(
            GuestsLimits.childAge.default,
          ),
        ]
        setChildrenAges(newAges)
      }

      // Скрываем блок если детей нет
      if (value < 1) {
        setShowChildrenBlock(false)
        setChildrenAges([])
        newAges = []
      }
      // Показываем блок если добавили детей
      else if (value >= 1 && !showChildrenBlock) {
        setShowChildrenBlock(true)
      }

      updateGuests(adults, value, newAges)
    },
    [adults, childrenAges, showChildrenBlock, updateGuests],
  )

  const handleChangeChildAge = useCallback(
    (index: number, age: number) => {
      const newAges = [...childrenAges]
      newAges[index] = age
      setChildrenAges(newAges)
      updateGuests(adults, children, newAges)
    },
    [adults, children, childrenAges, updateGuests],
  )

  const handleAddChild = useCallback(() => {
    setShowChildrenBlock(true)
    setChildren(1)
    const newAges = [GuestsLimits.childAge.default]
    setChildrenAges(newAges)
    updateGuests(adults, 1, newAges)
  }, [adults, updateGuests])

  const getGuestsState = useCallback((): GuestsState => {
    return {
      adults,
      children,
      childrenAges,
    }
  }, [adults, children, childrenAges])

  return {
    adults,
    children,
    childrenAges,
    showChildrenBlock,
    handleChangeAdults,
    handleChangeChildren,
    handleChangeChildAge,
    handleAddChild,
    getGuestsState,
  }
}
